import { ScreenLayout } from "../../layout/components";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { useNewGame } from "./useNewGame";
import { GroupHeader } from "../../components";
import { Button, Div, FormBuilder, H2, Icon, Label, Select } from "@grassrootly/rn-core";
import { newGameForm, gameTypeOptions, GameType, getGameTypeExplainer } from "./NewGame.types";
import SeasonSelector from "./SeasonSelector";
import { ScrollView } from "react-native";

export default function NewGameScreen({ navigation, route: { params } }: MainStackScreenProps<"new-game">) {
  const { 
    group, 
    creating, 
    onCreateGame, 
    gameType, 
    setGameType, 
    formLoading, 
    setFormLoading,
    seasonOptions,
    selectedSeasonId,
    setSelectedSeasonId,
    loadingSeasons,
    adminGroups,
    selectedGroupId,
    setSelectedGroupId,
    showGroupSelector
  } = useNewGame({
    navigation,
    groupId: params?.groupId,
  });
  return (
    <ScreenLayout 
      header={group ? () => <GroupHeader group={group} navigation={navigation} /> : {
        center: "New Match",
        left: <Icon name="arrow_back" size={24} onPress={() => navigation.goBack()} />
      }} 
      headerPaddingWeb={false}
    >
      <ScrollView 
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: 90, paddingBottom: 100, paddingHorizontal: 16 }}
        showsVerticalScrollIndicator={false}
      >
        <H2 classNames={["h4"]}>New Match</H2>
        
        {/* Group Selector - only show when no groupId is provided */}
        {showGroupSelector && (
          <Div classNames={["w100", "mt-{24}", "mb-{24}"]}>
            <Label classNames={["mb-{8}"]}>Select Group</Label>
            <Select
              selectedOption={selectedGroupId?.toString() || ""}
              onSelect={(option) => setSelectedGroupId(parseInt(option.value))}
              placeholderText="Choose a group to create a match for..."
              options={adminGroups.map(group => ({
                label: group.name,
                value: group.id.toString()
              }))}
            />
          </Div>
        )}
        
        <Div classNames={["w100", "mt-{24}"]}>
          <Div classNames={["flexRow", "gap-{12}", "alignCenter", "justifyCenter", "mb-{24}"]}>
            {gameTypeOptions.map((option) => (
              <Div classNames={["flexRow", "gap-{12}", "alignCenter", "justifyCenter"]} key={option.value}>
                <input
                  key={option.value}
                  type="radio"
                  id={option.value}
                  name="gameType"
                  checked={gameType === option.value}
                  style={{ margin: 0, fontFamily: "Exo2" }}
                  onChange={() => {
                    setFormLoading(true);
                    setGameType(option.value as GameType);
                    setTimeout(() => {
                      setFormLoading(false);
                    }, 0);
                  }}
                />
                <label
                  style={{ color: "#fff", cursor: "pointer", fontFamily: "Exo2" }}
                  htmlFor={option.value}
                  onClick={() => {
                    setFormLoading(true);
                    setGameType(option.value as GameType);
                    setTimeout(() => {
                      setFormLoading(false);
                    }, 0);
                  }}
                >
                  {option.label}
                </label>
              </Div>
            ))}
          </Div>
          
          {/* Game type explainer */}
          <Div classNames={["mb-{16}", "px-{16}", "py-{12}", "bg-gray-800", "rounded-{8}"]}>
            <p style={{ 
              color: "#9CA3AF", 
              fontSize: "14px", 
              fontFamily: "Exo2", 
              margin: 0, 
              lineHeight: "1.4" 
            }}>
              {getGameTypeExplainer(gameType)}
            </p>
          </Div>
          
          {!formLoading && !loadingSeasons && (!showGroupSelector || selectedGroupId) && (
            <>
              {/* Season selector only shows if there are seasons available */}
              <SeasonSelector
                seasonOptions={seasonOptions}
                selectedSeasonId={selectedSeasonId}
                onSeasonChange={setSelectedSeasonId}
                loading={loadingSeasons}
              />
              <FormBuilder
                form={newGameForm(gameType)}
                onSubmit={onCreateGame}
                submitControl={(submit) => (
                  <Button
                    title="Save"
                    classNames={["btnBlock", "mt-{24}"]}
                    isLoading={creating}
                    disabled={creating}
                    type="button"
                    onPress={submit}
                  />
                )}
              />
            </>
          )}
        </Div>
      </ScrollView>
    </ScreenLayout>
  );
}
